import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, LogOut, Plus, X, Settings, Shield, ShieldOff, Trash2, Key, Edit, Pencil, RefreshCw, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [editingPassword, setEditingPassword] = useState({});
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    blocked: false
  });
  const [siteBranding, setSiteBranding] = useState({
    site_title: "Classified Ads",
    logo_url: null,
    banner_url: null,
    logo_bg_color: "#000000"
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [ads, setAds] = useState([]);
  const [adDisplayOrders, setAdDisplayOrders] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchUsers();
    fetchSiteBranding();
    fetchAds();
    fetchPendingUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
      setServiceAreas(response.data.service_areas || []);
    } catch (error) {
      toast.error("Failed to fetch settings");
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      console.log("Fetching users with token:", token ? "present" : "missing");
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Users fetched:", response.data);
      // Handle both paginated and non-paginated response for backward compatibility
      const usersData = response.data.users || response.data;
      setUsers(usersData);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to fetch users");
    }
  };

  const fetchSiteBranding = async () => {
    try {
      const response = await axios.get(`${API}/settings/branding`);
      setSiteBranding(response.data);
    } catch (error) {
      console.error("Failed to fetch site branding:", error);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/users/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch pending users:", error);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User approved successfully!");
      fetchPendingUsers();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User rejected successfully!");
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject user");
    }
  };

  const handleAddCategory = () => {
    setCategories([...categories, { name: "", subcategories: [] }]);
  };

  const handleRemoveCategory = (index) => {
    const newCategories = categories.filter((_, i) => i !== index);
    setCategories(newCategories);
  };

  const handleCategoryNameChange = (index, value) => {
    const newCategories = [...categories];
    newCategories[index].name = value;
    setCategories(newCategories);
  };

  const handleAddSubcategory = (categoryIndex) => {
    const newCategories = [...categories];
    newCategories[categoryIndex].subcategories.push("");
    setCategories(newCategories);
  };

  const handleRemoveSubcategory = (categoryIndex, subcategoryIndex) => {
    const newCategories = [...categories];
    newCategories[categoryIndex].subcategories = newCategories[categoryIndex].subcategories.filter(
      (_, i) => i !== subcategoryIndex
    );
    setCategories(newCategories);
  };

  const handleSubcategoryChange = (categoryIndex, subcategoryIndex, value) => {
    const newCategories = [...categories];
    newCategories[categoryIndex].subcategories[subcategoryIndex] = value;
    setCategories(newCategories);
  };

  const handleAddServiceArea = () => {
    setServiceAreas([...serviceAreas, ""]);
  };

  const handleRemoveServiceArea = (index) => {
    const newServiceAreas = serviceAreas.filter((_, i) => i !== index);
    setServiceAreas(newServiceAreas);
  };

  const handleServiceAreaChange = (index, value) => {
    const newServiceAreas = [...serviceAreas];
    newServiceAreas[index] = value;
    setServiceAreas(newServiceAreas);
  };

  const handleSaveCategories = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Filter out empty categories and subcategories
      const validCategories = categories
        .filter(cat => cat.name.trim())
        .map(cat => ({
          name: cat.name.trim(),
          subcategories: cat.subcategories.filter(sub => sub.trim())
        }));

      await axios.put(`${API}/admin/settings/categories`, { categories: validCategories }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Categories saved successfully!");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save categories");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveServiceAreas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Filter out empty service areas
      const validServiceAreas = serviceAreas.filter(area => area.trim());

      await axios.put(`${API}/admin/settings/service-areas`, { service_areas: validServiceAreas }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Service areas saved successfully!");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service areas");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBlock = async (userId, currentBlockedStatus) => {
    console.log("Toggle block clicked for user:", userId, "current status:", currentBlockedStatus);

    try {
      const token = localStorage.getItem("token");
      console.log("Updating user block status...");
      await axios.put(`${API}/admin/users/${userId}`, 
        { blocked: !currentBlockedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("User block status updated successfully");
      toast.success(`User ${currentBlockedStatus ? 'unblocked' : 'blocked'} successfully!`);
      fetchUsers();
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    console.log("Delete clicked for user:", userId);

    try {
      const token = localStorage.getItem("token");
      console.log("Deleting user...");
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log("User deleted successfully");
      toast.success("User deleted successfully!");
      fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handlePasswordChange = (userId, password) => {
    setEditingPassword({ ...editingPassword, [userId]: password });
  };

  const handleSavePassword = async (userId) => {
    const newPassword = editingPassword[userId];
    console.log("Save password clicked for user:", userId, "password length:", newPassword?.length);
    if (!newPassword || newPassword.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      console.log("Updating password...");
      await axios.put(`${API}/admin/users/${userId}`, 
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log("Password updated successfully");
      toast.success("Password updated successfully!");
      setEditingPassword({ ...editingPassword, [userId]: "" });
    } catch (error) {
      console.error("Failed to update password:", error);
      toast.error(error.response?.data?.detail || "Failed to update password");
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResetPasswordData(response.data);
      setShowPasswordResetModal(true);
      setCopiedPassword(false);
      toast.success("Temporary password generated successfully!");
    } catch (error) {
      console.error("Failed to reset password:", error);
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const handleCopyPassword = async () => {
    if (!resetPasswordData?.temporary_password) return;

    const password = resetPasswordData.temporary_password;

    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(password);
        setCopiedPassword(true);
        toast.success("Password copied to clipboard!");
        setTimeout(() => setCopiedPassword(false), 2000);
        return;
      }
    } catch (err) {
      console.log("Modern clipboard API failed, trying fallback...");
    }

    // Fallback method using textarea and execCommand
    try {
      const textarea = document.createElement('textarea');
      textarea.value = password;
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        setCopiedPassword(true);
        toast.success("Password copied to clipboard!");
        setTimeout(() => setCopiedPassword(false), 2000);
      } else {
        throw new Error("execCommand failed");
      }
    } catch (err) {
      console.error("Failed to copy:", err);
      // If all else fails, just select the text so user can manually copy
      const passwordInput = document.querySelector('input[data-testid="temporary-password-display"]');
      if (passwordInput) {
        passwordInput.focus();
        passwordInput.select();
        toast.info("Please press Ctrl+C (or Cmd+C on Mac) to copy the password");
      } else {
        toast.error("Unable to copy automatically. Please select and copy manually.");
      }
    }
  };

  const handleClosePasswordModal = () => {
    setShowPasswordResetModal(false);
    setResetPasswordData(null);
    setCopiedPassword(false);
  };


  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("All fields are required");
      return;
    }
    
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("User created successfully!");
      setShowCreateUserModal(false);
      setNewUser({ name: "", email: "", password: "", blocked: false });
      fetchUsers();
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCreateUser = () => {
    setShowCreateUserModal(false);
    setNewUser({ name: "", email: "", password: "", blocked: false });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingLogo(true);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setSiteBranding({ ...siteBranding, logo_url: response.data.image_url });
      toast.success("Logo uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setSiteBranding({ ...siteBranding, logo_url: null });
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingBanner(true);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setSiteBranding({ ...siteBranding, banner_url: response.data.image_url });
      toast.success("Banner uploaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload banner");
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleRemoveBanner = () => {
    setSiteBranding({ ...siteBranding, banner_url: null });
  };

  const handleSaveBranding = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      await axios.put(`${API}/admin/settings/branding`, siteBranding, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Site branding updated successfully!");
      // Reload to update header
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save branding");
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/ads`);
      // Handle pagination response format
      const adsData = response.data.ads || response.data;
      setAds(adsData);
      // Initialize display orders
      const orders = {};
      adsData.forEach(ad => {
        orders[ad.id] = ad.display_order !== null && ad.display_order !== undefined ? ad.display_order : 9999;
      });
      setAdDisplayOrders(orders);
    } catch (error) {
      toast.error("Failed to fetch ads");
    }
  };

  const handleDisplayOrderChange = (adId, value) => {
    setAdDisplayOrders({
      ...adDisplayOrders,
      [adId]: parseInt(value) || 0
    });
  };

  const handleSaveDisplayOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      await axios.put(`${API}/ads/display-order/bulk`, adDisplayOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Display orders updated successfully!");
      await fetchAds(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update display orders");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAd = (adId) => {
    navigate(`/edit-ad/${adId}`);
  };

  const handleDeleteAd = async (adId) => {
    try {
      const token = localStorage.getItem("token");
      
      await axios.delete(`${API}/ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Ad deleted successfully!");
      await fetchAds(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete ad");
    }
  };

  return (
    <div className="min-h-screen bg-gray-200">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center">
                <Settings className="text-white w-6 h-6" />
              </div>
              <h1 className="text-2xl font-bold text-black">Admin Settings</h1>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-gray-600 text-sm sm:text-base">Welcome, {user?.name}</span>
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="logout-button"
                className="border-2 border-gray-300 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          data-testid="back-button"
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Categories Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-black">Categories & Subcategories</h2>
              <Button
                onClick={handleAddCategory}
                data-testid="add-category-button"
                className="bg-black text-white hover:bg-gray-800"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Category
              </Button>
            </div>

            <div className="space-y-6 max-h-[600px] overflow-y-auto">
              {categories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Input
                      value={category.name}
                      onChange={(e) => handleCategoryNameChange(categoryIndex, e.target.value)}
                      placeholder="Category name"
                      data-testid={`category-name-${categoryIndex}`}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveCategory(categoryIndex)}
                      data-testid={`remove-category-${categoryIndex}`}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="ml-4 space-y-2">
                    <Label className="text-sm text-gray-600">Subcategories:</Label>
                    {category.subcategories.map((subcategory, subcategoryIndex) => (
                      <div key={subcategoryIndex} className="flex items-center gap-2">
                        <Input
                          value={subcategory}
                          onChange={(e) =>
                            handleSubcategoryChange(categoryIndex, subcategoryIndex, e.target.value)
                          }
                          placeholder="Subcategory name"
                          data-testid={`subcategory-${categoryIndex}-${subcategoryIndex}`}
                          className="flex-1 h-9"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveSubcategory(categoryIndex, subcategoryIndex)}
                          data-testid={`remove-subcategory-${categoryIndex}-${subcategoryIndex}`}
                          className="h-9 w-9 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddSubcategory(categoryIndex)}
                      data-testid={`add-subcategory-${categoryIndex}`}
                      className="w-full text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Subcategory
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleSaveCategories}
              disabled={loading}
              data-testid="save-categories-button"
              className="w-full mt-6 bg-black text-white hover:bg-gray-800"
            >
              {loading ? "Saving..." : "Save Categories"}
            </Button>
          </div>

          {/* Service Areas Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-black">Service Areas</h2>
              <Button
                onClick={handleAddServiceArea}
                data-testid="add-service-area-button"
                className="bg-black text-white hover:bg-gray-800"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Area
              </Button>
            </div>

            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {serviceAreas.map((serviceArea, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={serviceArea}
                    onChange={(e) => handleServiceAreaChange(index, e.target.value)}
                    placeholder="Service area name"
                    data-testid={`service-area-${index}`}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveServiceArea(index)}
                    data-testid={`remove-service-area-${index}`}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              onClick={handleSaveServiceAreas}
              disabled={loading}
              data-testid="save-service-areas-button"
              className="w-full mt-6 bg-black text-white hover:bg-gray-800"
            >
              {loading ? "Saving..." : "Save Service Areas"}
            </Button>
          </div>
        </div>

        {/* Site Branding Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-black mb-6">Site Branding</h2>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="site-title" className="text-sm font-medium text-gray-700">
                Page Title
              </Label>
              <Input
                id="site-title"
                type="text"
                value={siteBranding.site_title}
                onChange={(e) => setSiteBranding({ ...siteBranding, site_title: e.target.value })}
                placeholder="Classified Ads"
                data-testid="site-title-input"
                className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
              />
              <p className="text-xs text-gray-500">This will appear in the header and page titles</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Header Logo
              </Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6">
                {siteBranding.logo_url ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${siteBranding.logo_url}`}
                        alt="Site logo"
                        className="max-h-24 rounded-lg shadow-lg"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRemoveLogo}
                      className="w-full"
                      data-testid="remove-logo-button"
                    >
                      Remove Logo
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      className="hidden"
                      id="logo-upload"
                      data-testid="logo-upload-input"
                    />
                    <Label
                      htmlFor="logo-upload"
                      className="cursor-pointer inline-flex flex-col items-center"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-2xl">🖼️</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {uploadingLogo ? "Uploading..." : "Click to upload logo"}
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        PNG, JPG, GIF up to 5MB
                      </span>
                    </Label>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">Logo will replace "ESMC" in header. Recommended size: 48x48 pixels</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo-bg-color" className="text-sm font-medium text-gray-700">
                Logo Background Color
              </Label>
              <div className="flex items-center gap-4">
                <Input
                  id="logo-bg-color"
                  type="color"
                  value={siteBranding.logo_bg_color || "#000000"}
                  onChange={(e) => setSiteBranding({ ...siteBranding, logo_bg_color: e.target.value })}
                  className="h-12 w-24 cursor-pointer"
                  data-testid="logo-bg-color-input"
                />
                <div className="flex-1">
                  <Input
                    type="text"
                    value={siteBranding.logo_bg_color || "#000000"}
                    onChange={(e) => setSiteBranding({ ...siteBranding, logo_bg_color: e.target.value })}
                    placeholder="#000000"
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors font-mono"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">Background color for the logo container in header</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Banner Image
              </Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-6">
                {siteBranding.banner_url ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-center">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${siteBranding.banner_url}`}
                        alt="Banner preview"
                        className="max-h-32 w-full object-cover rounded-lg shadow-lg"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRemoveBanner}
                      className="w-full"
                      data-testid="remove-banner-button"
                    >
                      Remove Banner
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerUpload}
                      disabled={uploadingBanner}
                      className="hidden"
                      id="banner-upload"
                      data-testid="banner-upload-input"
                    />
                    <Label
                      htmlFor="banner-upload"
                      className="cursor-pointer inline-flex flex-col items-center"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                        <span className="text-2xl">🖼️</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {uploadingBanner ? "Uploading..." : "Click to upload banner"}
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        PNG, JPG, GIF up to 5MB
                      </span>
                    </Label>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">Banner appears above search on dashboard. Recommended size: 1200x300 pixels</p>
            </div>

            <Button
              onClick={handleSaveBranding}
              disabled={loading}
              data-testid="save-branding-button"
              className="w-full bg-black text-white hover:bg-gray-800"
            >
              {loading ? "Saving..." : "Save Site Branding"}
            </Button>
          </div>
        </div>

        {/* Pending Users Approval Section */}
        {pendingUsers.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-black">Pending User Approvals</h2>
              <span className="text-sm text-gray-600">{pendingUsers.length} pending</span>
            </div>
            
            <div className="space-y-4">
              {pendingUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div>
                    <p className="font-medium text-black">{user.name}</p>
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Registered: {new Date(user.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApproveUser(user.id)}
                      className="bg-green-600 text-white hover:bg-green-700"
                      size="sm"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleRejectUser(user.id)}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      size="sm"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ad Management Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-black">Ad Management</h2>
            <span className="text-sm text-gray-600">{ads.length} total ads</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Title</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Category</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Created</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Display Order</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr key={ad.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium">
                      {ad.title}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {ad.general_category || 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(ad.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <Input
                        type="number"
                        value={adDisplayOrders[ad.id] || 0}
                        onChange={(e) => handleDisplayOrderChange(ad.id, e.target.value)}
                        className="h-8 text-sm text-center max-w-[100px] mx-auto"
                        data-testid={`display-order-${ad.id}`}
                        min="0"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditAd(ad.id)}
                          className="h-8 border-gray-300 hover:bg-gray-100"
                          data-testid={`edit-ad-${ad.id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteAd(ad.id)}
                          className="h-8 border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`delete-ad-${ad.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Button
            onClick={handleSaveDisplayOrders}
            disabled={loading}
            data-testid="save-display-orders-button"
            className="w-full mt-6 bg-black text-white hover:bg-gray-800"
          >
            {loading ? "Saving..." : "Save Display Orders"}
          </Button>
        </div>

        {/* User Management Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-black">User Management</h2>
            <Button
              onClick={() => setShowCreateUserModal(true)}
              data-testid="create-user-button"
              className="bg-black text-white hover:bg-gray-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New User
            </Button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Created</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Password</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{u.name}</td>
                    <td className="py-3 px-4 text-sm">{u.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      {u.blocked ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-100 text-red-700">
                          <ShieldOff className="w-3 h-3 mr-1" />
                          Blocked
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                          <Shield className="w-3 h-3 mr-1" />
                          Active
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Input
                          type="password"
                          placeholder="New password"
                          value={editingPassword[u.id] || ""}
                          onChange={(e) => handlePasswordChange(u.id, e.target.value)}
                          className="h-8 text-sm flex-1"
                          data-testid={`password-input-${u.id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleSavePassword(u.id)}
                          disabled={!editingPassword[u.id] || editingPassword[u.id].length < 6}
                          className="h-8 bg-blue-600 hover:bg-blue-700"
                          data-testid={`save-password-${u.id}`}
                        >
                          <Key className="w-3 h-3" />
                        </Button>
                        {u.email !== user?.email && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetPassword(u.id)}
                            className="h-8 border-purple-600 text-purple-600 hover:bg-purple-50"
                            data-testid={`reset-password-${u.id}`}
                            title="Generate temporary password"
                          >
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {u.email !== user?.email && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleBlock(u.id, u.blocked)}
                              className={`h-8 ${u.blocked ? 'border-green-600 text-green-600 hover:bg-green-50' : 'border-orange-600 text-orange-600 hover:bg-orange-50'}`}
                              data-testid={`toggle-block-${u.id}`}
                            >
                              {u.blocked ? <Shield className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(u.id)}
                              className="h-8 text-red-600 hover:bg-red-50"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {u.email === user?.email && (
                          <span className="text-xs text-gray-400 italic">Current User</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <p className="text-center py-8 text-gray-500">No users found</p>
            )}
          </div>
        </div>

        {/* Create User Modal */}
        {showCreateUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-black mb-4">Create New User</h3>
              
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-user-name" className="text-sm font-medium text-gray-700">
                    Road Name
                  </Label>
                  <Input
                    id="new-user-name"
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder=""
                    required
                    data-testid="new-user-name-input"
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-user-email" className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    id="new-user-email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                    data-testid="new-user-email-input"
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-user-password" className="text-sm font-medium text-gray-700">
                    Password
                  </Label>
                  <Input
                    id="new-user-password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="new-user-password-input"
                    className="h-12 border-2 border-gray-200 focus:border-black transition-colors"
                  />
                  <p className="text-xs text-gray-500">Minimum 6 characters</p>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="new-user-blocked"
                    checked={newUser.blocked}
                    onChange={(e) => setNewUser({ ...newUser, blocked: e.target.checked })}
                    data-testid="new-user-blocked-checkbox"
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor="new-user-blocked" className="text-sm text-gray-700 cursor-pointer">
                    Block this user immediately
                  </Label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-create-user"
                    className="flex-1 h-12 bg-black text-white hover:bg-gray-800 transition-colors"
                  >
                    {loading ? "Creating..." : "Create User"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelCreateUser}
                    disabled={loading}
                    data-testid="cancel-create-user"
                    className="flex-1 h-12 border-2 border-gray-300 hover:bg-gray-100"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* Password Reset Modal */}
        {showPasswordResetModal && resetPasswordData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-black">Password Reset Successful</h3>
                <button
                  onClick={handleClosePasswordModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>User:</strong> {resetPasswordData.user_name} ({resetPasswordData.user_email})
                  </p>
                  <p className="text-sm text-blue-800">
                    A temporary password has been generated. Please share this with the user securely.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Temporary Password
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={resetPasswordData.temporary_password}
                      readOnly
                      onClick={(e) => e.target.select()}
                      className="h-12 font-mono text-lg border-2 border-gray-300 bg-gray-50 cursor-pointer"
                      data-testid="temporary-password-display"
                      title="Click to select, then press Ctrl+C to copy"
                    />
                    <Button
                      onClick={handleCopyPassword}
                      className={`h-12 ${copiedPassword ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                      data-testid="copy-password-button"
                    >
                      {copiedPassword ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium mb-1">⚠️ Important:</p>
                  <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
                    <li>This password is only shown once</li>
                    <li>The user should change it after first login</li>
                    <li>Share this password securely with the user</li>
                  </ul>
                </div>

                <Button
                  onClick={handleClosePasswordModal}
                  className="w-full h-12 bg-black text-white hover:bg-gray-800"
                  data-testid="close-password-modal"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default AdminSettings;
